import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Loader2, ChevronDown, ChevronRight, Trophy } from "lucide-react";

const thisMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const fmtDay = (ds) => new Date(ds + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });

const Stat = ({ v, cls, tid }) => <td className={`px-3 py-2 text-center tabular-nums font-semibold ${v ? cls : "text-muted-foreground"}`} data-testid={tid}>{v}</td>;

const DayList = ({ p }) => (
  <tr><td colSpan={7} className="bg-muted/30 px-6 py-3" data-testid={`scoreboard-days-${p.property_id}`}>
    {p.days.length === 0 ? <span className="text-xs text-muted-foreground">No late or missed days.</span> : (
      <div className="flex flex-wrap gap-2">
        {p.days.map((d) => (
          <Link key={d.date} to={`/daily-reports/${p.property_id}/${d.date}`} data-testid={`scoreboard-day-${p.property_id}-${d.date}`}
            className={`border px-2.5 py-1 text-xs transition-colors hover:bg-secondary ${d.status === "missed" ? "border-red-400 text-red-700 dark:text-red-400" : "border-amber-400 text-amber-700 dark:text-amber-400"}`}>
            <b>{fmtDay(d.date)}</b> · {d.status === "missed" ? (d.state === "draft" ? "Missed (draft)" : "Missed") : `Late ${d.hours_late}h${d.approx ? "*" : ""}${d.submitted_by ? ` · ${d.submitted_by}` : ""}`}
          </Link>
        ))}
      </div>
    )}
  </td></tr>
);

export const LateReportScoreboard = () => {
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [open, setOpen] = useState({});
  useEffect(() => {
    if (!month) return;
    setData(null);
    api.get("/daily-reports/scoreboard", { params: { month } }).then((r) => setData(r.data)).catch(() => setData({ properties: [] }));
  }, [month]);
  return (
    <Card className="rounded-none border p-6" data-testid="late-report-scoreboard">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 font-heading text-lg font-bold"><Trophy className="h-5 w-5 text-primary" /> Late Report Scoreboard</div>
          <p className="text-xs text-muted-foreground">Late = submitted after 1 PM (property time) the next day. Missed = past the deadline and still not submitted. Worst offenders first.</p>
        </div>
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 w-44" data-testid="scoreboard-month" />
      </div>
      {!data ? <div className="flex h-32 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="scoreboard-table">
            <thead><tr className="border-b border-border text-left label-caps text-[0.6rem] text-muted-foreground">
              <th className="px-3 py-2">Property</th><th className="px-3 py-2 text-center">On time</th><th className="px-3 py-2 text-center">Late</th>
              <th className="px-3 py-2 text-center">Missed</th><th className="px-3 py-2 text-center">Pending</th><th className="px-3 py-2 text-center">Backfilled</th><th className="px-3 py-2 text-center">On-time %</th>
            </tr></thead>
            <tbody>
              {data.properties.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">No properties.</td></tr>}
              {data.properties.map((p) => [
                <tr key={p.property_id} className="cursor-pointer border-b border-border transition-colors hover:bg-secondary/60" onClick={() => setOpen((o) => ({ ...o, [p.property_id]: !o[p.property_id] }))} data-testid={`scoreboard-row-${p.property_id}`}>
                  <td className="px-3 py-2 font-semibold"><span className="inline-flex items-center gap-1">{open[p.property_id] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}{p.property_name}</span></td>
                  <Stat v={p.on_time} cls="text-emerald-600" tid={`scoreboard-ontime-${p.property_id}`} />
                  <Stat v={p.late} cls="text-amber-600" tid={`scoreboard-late-${p.property_id}`} />
                  <Stat v={p.missed} cls="text-red-600" tid={`scoreboard-missed-${p.property_id}`} />
                  <Stat v={p.pending} cls="" tid={`scoreboard-pending-${p.property_id}`} />
                  <Stat v={p.backfilled} cls="" tid={`scoreboard-backfilled-${p.property_id}`} />
                  <td className="px-3 py-2 text-center tabular-nums" data-testid={`scoreboard-pct-${p.property_id}`}>{p.on_time_pct == null ? "—" : `${p.on_time_pct}%`}</td>
                </tr>,
                open[p.property_id] && <DayList key={`${p.property_id}-d`} p={p} />,
              ])}
            </tbody>
          </table>
          <p className="mt-2 text-[0.7rem] text-muted-foreground">* Older reports without a recorded submit time use their last-saved time. Backfilled = entered through Monthly Entry.</p>
        </div>
      )}
    </Card>
  );
};
